import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  getProviderById,
  getProductsByProviderId,
} from "../../../../utility/getMarketplaceData.js";
import { PLACEHOLDER_IMAGES } from "../../../../utility/placeholders";
import MarketplaceProductCard from "../../../../components/marketplace/MarketplaceProductCard";
import LoadingSpinner from "../../../../components/LoadingSpinner.js";
import CartButton from "../../../../components/marketplace/CartButton/CartButton";
import ProductModal from "./ProductModal";
import { getRequestItems } from "../../../../redux/marketplaceRequest/reducer";
import "./ProviderInfo.scss";
import { ReactComponent as LeftArrow } from "../../../../images/left-arrow.svg";
import { ReactComponent as Certification } from "../../../../images/Certification.svg";

const ProviderInfo = () => {
  const navigate = useNavigate();
  const { providerId } = useParams();
  const { t } = useTranslation("marketplace");
  const [provider, setProvider] = useState(null);
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const requestItems = useSelector(getRequestItems);

  useEffect(() => {
    const loadProviderAndProducts = async () => {
      try {
        setIsLoading(true);
        const providerData = await getProviderById(providerId);
        setProvider(providerData);

        if (!providerData) {
          navigate("/dashboard/marketplace");
          return;
        }

        const providerProducts = await getProductsByProviderId(providerId);
        setProducts(providerProducts);
      } catch (error) {
        console.error("Error loading provider data:", error);
        navigate("/dashboard/marketplace");
      } finally {
        setIsLoading(false);
      }
    };

    loadProviderAndProducts();
  }, [providerId, navigate]);

  if (isLoading) {
    return (
      <div className="container-fluid provider-info">
        <div className="card themeCard">
          <div
            className="card-body p-sm-3"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "400px",
            }}
          >
            <LoadingSpinner width="50px" />
          </div>
        </div>
      </div>
    );
  }

  if (!provider) return null;

  const images =
    provider.images && Array.isArray(provider.images) ? provider.images : [provider.imageUrl];
  const currentImage = images[currentImageIndex] || provider.imageUrl;
  const hasMultipleImages = images.length > 1;
  const descriptionParagraphs = (provider.fullDescription || provider.description || "")
    .split("\n\n")
    .filter(Boolean);
  const selectedProductIds = new Set(requestItems.map((item) => item.id));
  const providerCategoryLabel = provider.categoryId
    ? t(`category_names.${provider.categoryId}`, { defaultValue: provider.categoryId })
    : provider.categoryName || "";

  const goToNextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const goToPrevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const goToImageAtIndex = (index) => {
    setCurrentImageIndex(index);
  };

  return (
    <div className="container-fluid provider-info">
      <div className="card themeCard">
        <div className="card-body p-sm-3">
          <div className="provider-info__page-header">
            <button
              className="provider-info__back-btn"
              onClick={() => navigate("/dashboard/marketplace")}
              type="button"
            >
              <LeftArrow />
            </button>
            <div className="provider-info__page-title">
              <span>{t("provider.back_to_marketplace", { defaultValue: "Marketplace" })}</span>
              <h3>{provider.name}</h3>
            </div>
            <CartButton />
          </div>

          <div className="provider-info__details">
            <div className="provider-info__image">
              <img
                src={currentImage}
                alt={provider.name}
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = PLACEHOLDER_IMAGES.PROVIDER;
                }}
              />
              {hasMultipleImages && (
                <>
                  <button
                    className="provider-info__carousel-btn provider-info__carousel-btn--prev"
                    onClick={goToPrevImage}
                    aria-label="Image précédente"
                  >
                    ‹
                  </button>
                  <button
                    className="provider-info__carousel-btn provider-info__carousel-btn--next"
                    onClick={goToNextImage}
                    aria-label="Image suivante"
                  >
                    ›
                  </button>
                  <div className="provider-info__carousel-indicators">
                    {images.map((_, index) => (
                      <button
                        key={index}
                        className={`provider-info__indicator ${
                          index === currentImageIndex ? "provider-info__indicator--active" : ""
                        }`}
                        onClick={() => goToImageAtIndex(index)}
                        aria-label={`Image ${index + 1}`}
                        aria-current={index === currentImageIndex}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="provider-info__content">
              {providerCategoryLabel && (
                <span className="provider-info__category">{providerCategoryLabel}</span>
              )}
              <h3 className="provider-info__provider-name">{provider.name}</h3>
              <div className="provider-info__description">
                {descriptionParagraphs.length > 0 ? (
                  descriptionParagraphs.map((paragraph, i) => <p key={i}>{paragraph}</p>)
                ) : (
                  <p>{t("provider.no_description", { defaultValue: "Aucune description." })}</p>
                )}
              </div>
              {provider.message && <div className="provider-info__message">{provider.message}</div>}
              <div className="provider-info__badges">
                {provider.badges?.map((badge, i) => (
                  <div key={i} className="provider-info__badge">
                    <Certification />
                    {badge}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="provider-info__products">
            <div className="provider-info__products-header">
              <div>
                <span>{t("provider.products_eyebrow", { defaultValue: "Sélection" })}</span>
                <h3 className="provider-info__products-title">{t("provider.our_products")}</h3>
              </div>
              <p>
                {t("provider.products_count", {
                  count: products.length,
                  defaultValue: "{{count}} produit(s) disponible(s)",
                })}
              </p>
            </div>
            {products.length > 0 ? (
              <div className="provider-info__products-grid">
                {products.map((product) => (
                  <MarketplaceProductCard
                    key={product.id}
                    product={product}
                    providerName={provider.name}
                    isSelected={selectedProductIds.has(product.id)}
                    onClick={() => setSelectedProduct(product)}
                  />
                ))}
              </div>
            ) : (
              <p className="provider-info__no-products">{t("provider.no_products")}</p>
            )}
          </div>
        </div>
      </div>

      {selectedProduct && (
        <ProductModal
          product={selectedProduct}
          provider={provider}
          onClose={() => setSelectedProduct(null)}
        />
      )}
    </div>
  );
};

export default ProviderInfo;
